import React, { useState } from "react";
import { Tab, Nav, NavDropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { getAdminPathForTab, getAdminTabFromPath } from "../../config/adminRoutes";
import TopNav from "../TopNav/TopNav";
import AdminNav from "./AdminNav";
import ApiTester from "./AdminAPITest";
import AdminManageFieldNotify from "../../AdminPage/AdminManageFieldNotify";
import DropdownValues from "./DropdownValues";
import UserManage from "./UserManage";
import AdminManualCheckout from "./AdminManualCheckout";
import AdminCheckedOutJobs from "./AdminCheckedOutJobs";
import AdminJobVisibility from "./AdminJobVisibility";
import FormatJobDetails from "./FormatJobDetails";
import FormFieldBuilder from "./FormFieldBuilder";
import AdminFieldDesc from "./AdminFieldDesc"; 
import AdminInfoBubbles from "./AdminInfoBubbles";
import AdminGlobalVariables from "./AdminGlobalVariables";
import AdminManageRoles from "./AdminManageRoles";
import AdminUserPreferences from "./AdminUserPreferences";
import AdminDynamicTableEditor from "./AdminDynamicTableEditor"; // Import the dynamic table editor
import AdminComponentPolicies from "./AdminComponentPolicies";
import AdminFieldSearch from "./AdminFieldSearch"; // Import the field search component
import SharePointDocumentBrowser from "./SharePointDocumentBrowser"; // Import the SharePoint document browser
import AdminHome from "./AdminHome";
import AdminHelpManager from "../../help/AdminHelpManager";
import HelpDrawer from "../../help/HelpDrawer";
import AdminTimeComparison from "../../AdminPage/AdminTimeComparison";
import AdminFormManager from "./AdminFormManager";
import AdminSignalRTest from "./AdminSignalRTest";
import { usePermissions } from "../../hooks/usePermissions";

function AdminContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const key = getAdminTabFromPath(location.pathname);
  const navigateToTab = (tabKey) => navigate(getAdminPathForTab(tabKey));
  const [refreshCheckedOut, setRefreshCheckedOut] = useState(false);
  const userStatus = useSelector((state) => state.user?.status);
  const isAdminRole = useSelector((state) => state.user?.rolesMapped?.Admin === true);
  const { canView, isLoading: isPermissionsLoading } = usePermissions();
  const isLoading = userStatus === "idle" || userStatus === "loading" || isPermissionsLoading;
  const hasAdminAccess = canView("admin.container");

  // Function to trigger refresh for AdminCheckedOut component
  const triggerRefresh = () => {
    setRefreshCheckedOut((prev) => !prev); // Toggle the state to force re-render
  };

  const tabGroups = [
    {
      title: "Users & Access",
      tabs: [
        {
          eventKey: "users",
          title: "Users",
          content: <UserManage />,
        },
        {
          eventKey: "customRoles",
          title: "User Roles",
          content: <AdminManageRoles />,
        },
        {
          eventKey: "componentPolicies",
          title: "Component Policies",
          permissionKey: "admin.component-policies",
          content: <AdminComponentPolicies />,
        },
      ],
    },
    {
      title: "Job Management",
      tabs: [
        {
          eventKey: "timeComparison",
          title: "Allocated vs Actual Time",
          content: key === "timeComparison" ? <AdminTimeComparison /> : null,
        },
        {
          eventKey: "jobs",
          title: "Manage Jobs",
          content: (
            <>
              <div className="mb-4">
                <AdminManualCheckout onJobCheckedOut={triggerRefresh} />
              </div>
              <div className="mb-4">
                <AdminCheckedOutJobs refresh={refreshCheckedOut} />
              </div>
              <div className="mb-4">
                <AdminJobVisibility />
              </div>
            </>
          ),
        },
      ],
    },
    {
      title: "Fields & Content",
      tabs: [
        {
          eventKey: "help",
          title: "Help Manager",
          permissionKey: "admin.help-manager",
          content: <AdminHelpManager />,
        },
        {
          eventKey: "formManager",
          title: "Form Manager",
          content: <AdminFormManager />,
        },
        {
          eventKey: "fieldNotify",
          title: "Field Notifications",
          content: <AdminManageFieldNotify />,
        },
        {
          eventKey: "dropdowns",
          title: "Dropdowns",
          content: <DropdownValues />,
        },
        {
          eventKey: "fieldDescriptions",
          title: "Field Descriptions",
          content: <AdminFieldDesc />,
        },
        {
          eventKey: "infoBubbles",
          title: "Info Bubbles",
          content: <AdminInfoBubbles />,
        },
      ],
    },
    {
      title: "Builders & Tools",
      tabs: [
        {
          eventKey: "components",
          title: "Create Components",
          content: (
            <>
              <div className="mb-4">
                <FormatJobDetails />
              </div>
              <div className="mb-4">
                <FormFieldBuilder />
              </div>
            </>
          ),
        },
        {
          eventKey: "dynamicTableEditor",
          title: "Dynamic Table Editor",
          permissionKey: "admin.dynamic-table-editor",
          content: <AdminDynamicTableEditor />,
        },
        {
          eventKey: "fieldSearch",
          title: "Field Search",
          content: <AdminFieldSearch />,
        },
        {
          eventKey: "apiTest",
          title: "API Test",
          content: <ApiTester />,
        },
        {
          eventKey: "signalrTest",
          title: "SignalR Test",
          permissionKey: "admin.signalr-test",
          content: key === "signalrTest" ? <AdminSignalRTest /> : null,
        },
      ],
    },
    {
      title: "System",
      tabs: [
        {
          eventKey: "globalVariables",
          title: "Global Variables",
          permissionKey: "admin.global-variables",
          content: <AdminGlobalVariables />,
        },
        {
          eventKey: "userPreferences",
          title: "User Preferences",
          permissionKey: "admin.user-preferences",
          content: <AdminUserPreferences />,
        },
        {
          eventKey: "sharepoint",
          title: "SharePoint Documents",
          content: <SharePointDocumentBrowser />,
        },
      ],
    },
  ];

  const sortedTabGroups = tabGroups
    .map((group) => ({
      ...group,
      tabs: group.tabs.filter((tab) => (tab.eventKey !== 'help' || isAdminRole) &&
        (!tab.permissionKey || canView(tab.permissionKey))),
    }))
    .filter((group) => group.tabs.length > 0)
    .map((group) => ({
      ...group,
      tabs: [...group.tabs].sort((a, b) => a.title.localeCompare(b.title)),
    }))
    .sort((a, b) => a.title.localeCompare(b.title));

  const allTabs = sortedTabGroups.flatMap((group) => group.tabs);
  const activeKey = key === "home" || allTabs.some((tab) => tab.eventKey === key)
    ? key
    : "home";

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!hasAdminAccess) {
    return <Navigate to="/" replace />;
  }

  const activePath = getAdminPathForTab(activeKey);
  if (location.pathname !== activePath) {
    return <Navigate to={`${activePath}${location.search}${location.hash}`} replace />;
  }

  return (
    <div>
      <TopNav />
      <AdminNav />
      <HelpDrawer />
      <div className="container-fluid mt-3">
        <Tab.Container activeKey={activeKey}>
          <>
            <Nav variant="tabs" className="mb-3 flex-wrap">
              <Nav.Item>
                <Nav.Link as={Link} to={getAdminPathForTab('home')} eventKey="home">Home</Nav.Link>
              </Nav.Item>

              {sortedTabGroups.map((group) => (
                <NavDropdown key={group.title} title={group.title} id={`admin-${group.title.replace(/\s+/g, "-").toLowerCase()}-tabs`}>
                  {group.tabs.map((tab) => (
                    <NavDropdown.Item as={Link} to={getAdminPathForTab(tab.eventKey)} key={tab.eventKey} eventKey={tab.eventKey}>
                      {tab.title}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              ))}
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="home" aria-label="Admin Home">
                <AdminHome
                  onNavigate={navigateToTab}
                  availableTabKeys={allTabs.map((tab) => tab.eventKey)}
                />
              </Tab.Pane>

              {allTabs.map((tab) => (
                <Tab.Pane key={tab.eventKey} eventKey={tab.eventKey} aria-label={tab.title}>
                  {tab.content}
                </Tab.Pane>
              ))}
            </Tab.Content>
          </>
        </Tab.Container>
      </div>
    </div>
  );
}

export default AdminContainer;
